import { IconRefresh } from "@/assets/icons/geist/IconRefresh";
import { IconWarning } from "@/assets/icons/geist/IconWarning";
import { ErrorMessage } from "@/components/ErrorMessage";
import { VisibilityControlledByFeatureFlag } from "@/components/VisibilityControlledByFeatureFlag";
import { Button } from "@/components/flexkit/Button";
import { Text } from "@/components/podkit/typography/Text";
import { RunnerActionsDropdown } from "@/components/runners/RunnerActionsDropdown";
import { ShareRunnerButton } from "@/components/runners/ShareRunnerButton";
import { RunnerDetails } from "@/components/runners/details/RunnerDetails";
import { RunnerGroupAccess } from "@/components/runners/details/RunnerGroupAccess";
import { SettingsPageContext } from "@/contexts/SettingsPageContext";
import { Flags } from "@/feature-flags";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useRunner } from "@/queries/runner-queries";
import { Code } from "@connectrpc/connect";
import IconDotGrid1x3Horizontal from "central-icons/IconDotGrid1x3Horizontal";
import { useCallback, useContext, useEffect, type FC } from "react";
import { useNavigate, useParams } from "react-router-dom";

export const RunnerDetailsPage: FC<{ runnerId?: string }> = (p) => {
    const params = useParams();
    useDocumentTitle("Runner - Details");

    const runnerId = p.runnerId ?? params.runnerId;

    const { data: runner, error, refetch } = useRunner(runnerId);
    const { setBreadCrumbRowAction, setCustomBreadcrumbs } = useContext(SettingsPageContext);
    const navigate = useNavigate();

    const onDeleteSuccessful = useCallback(() => {
        navigate("/settings/runners");
    }, [navigate]);

    useEffect(() => {
        if (!runner) return;

        setBreadCrumbRowAction(
            <VisibilityControlledByFeatureFlag featureFlag={Flags.GroupManagementEnabled}>
                <div className="flex items-center gap-3">
                    <RunnerGroupAccess runnerId={runner.runnerId} />
                    <ShareRunnerButton runnerId={runner.runnerId} />
                    <RunnerActionsDropdown
                        runner={runner}
                        onDeleteSuccessful={onDeleteSuccessful}
                        showViewDetails={false}
                        button={
                            <Button
                                variant="ghost"
                                className="size-6 border-0 p-0"
                                data-testid="runner-actions-dropdown"
                                aria-label="More actions"
                            >
                                <IconDotGrid1x3Horizontal className="size-4 text-content-primary" />
                            </Button>
                        }
                    />
                </div>
            </VisibilityControlledByFeatureFlag>,
        );

        setCustomBreadcrumbs([
            { label: "Runners", to: "/settings/runners" },
            { label: runner.name, description: runner.spec?.configuration?.region },
        ]);

        return () => {
            setBreadCrumbRowAction(null);
            setCustomBreadcrumbs(null);
        };
    }, [onDeleteSuccessful, runner, setBreadCrumbRowAction, setCustomBreadcrumbs]);

    if (error) {
        return <FailedToLoadRunner runnerId={runnerId} error={error} onRefresh={refetch} />;
    }

    return <RunnerDetails runner={runner} />;
};

const FailedToLoadRunner: FC<{ runnerId?: string; error: Error; onRefresh: () => void }> = ({
    runnerId,
    error,
    onRefresh,
}) => {
    return (
        <div className="flex min-h-72 flex-col items-center justify-center gap-4 rounded-xl border border-border-light bg-surface-primary">
            <div className="flex flex-col items-center gap-1">
                <IconWarning size="lg" className="text-content-negative" />
                <Text className="text-lg font-bold text-content-negative">
                    We&#x2019;re having trouble loading this runner.
                </Text>
                <Text className="text-center text-base text-content-negative">
                    You can try refreshing the page. If the problem persists, please contact support.
                </Text>
                <Text className="text-sm text-content-negative">Runner ID: {runnerId}</Text>
                <ErrorMessage
                    error={error}
                    className="text-sm"
                    messages={{
                        [Code.InvalidArgument]: "Invalid runner ID",
                    }}
                />
            </div>
            <div>
                <Button
                    type="button"
                    variant="secondary"
                    onClick={onRefresh}
                    data-tracking-id="refresh-runner-details-page"
                >
                    <IconRefresh size="base" />
                    Refresh
                </Button>
            </div>
        </div>
    );
};
